import { OnInit, OnDestroy, Component, ElementRef, ViewChild, ViewContainerRef, ComponentFactoryResolver } from '@angular/core';
import { DocumentService } from '../document.service';
import { Configuration } from '../../conf/app.constants';
import { PlycaDataService } from '../../../services/plyca.service';
import { PaginationDTO, PaginationInfo } from '../../api/PaginationModel';
import { HelpType, HelpService } from '../../components/common/help/help.service';
import { WaitLoadingService } from '../../components/common/waitLoading/wait-loading.service';
import { HeaderActionsService } from '../../components/common/sub-header/header-actions.service';
import { UIService } from '../../ui.service';
import { UserService } from '../../user.service';
import { DocumentDTO, UserDTO, MasterDataDTO, OrganizationalUnitDTO, DivisionDTO, getSimpleIdentifierFromString } from '../../api/plyca-dtos';
import { HttpParams } from '@angular/common/http';
import { documentViewerComponent } from '../documentViewer/documentViewer.component';
import { formPlycaComponent } from '../../components/forms/formPlyca.component';
import { FormConfig } from '../../components/forms/form.objects';
import { catchError, finalize, take } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { UserPrefs, DossiersViewPrefs } from '../../api/UserPrefs-dto';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/AppState';
import { SET_USERPREFS } from '../../store/userPrefs';
import * as lodash from 'lodash';
import { BaseComponent } from '../../components/common/baseComponent/base.component';
import { DialogService, DynamicDialogConfig } from 'primeng-lts/dynamicdialog';
import { UserPrefsService } from '../../store/user-prefs.service';

export class DocumentSearchFiltersDTO {
  description: string;
  division: DivisionDTO;
  divisionType: string;
  documentClasses: string[];
  documentFolder: string;

  dossierName: string;
  dossierId: string;
  contractType: MasterDataDTO;
  procedureType: MasterDataDTO;
  organizationalUnitId: OrganizationalUnitDTO;

  stateFrom: Date;
  stateTo: Date;
  stateId: string;
  userStateId: UserDTO;

  signatureFrom: Date;
  signatureTo: Date;
  userSignatureId: UserDTO;

  winnerPartyId: string;
  winnerParty: string;

  includeCancelled: boolean;
  ignoreNotifications: boolean;

  constructor() {
    this.includeCancelled = false;
    this.ignoreNotifications = false;
  }
}

export class DocumentSearchStateDTO {
  pageInfo: PaginationInfo;
  _filters: DocumentSearchFiltersDTO;

  constructor() {
    this.pageInfo = new PaginationInfo();
    this.pageInfo.size = 25;
    this.pageInfo.number = 0;

    this._filters = new DocumentSearchFiltersDTO();
  }
}

@Component({
  selector: 'documentSearch',
  templateUrl: 'documentSearch.template.html'
  // ,providers: [DialogService]
  , providers: [DatePipe]
})
export class DocumentSearchComponent extends BaseComponent implements OnInit, OnDestroy {
  displayFilters = false;
  loaded = false;
  error = false;
  searchPerformed = false;

  state: DocumentSearchStateDTO;
  filters: DocumentSearchFiltersDTO;
  activeFilters: Array<any> = [];
  // numFilters = 0; // variable de apoyo para reducir llamadas a la funcion availablefilters
  documents: Array<DocumentDTO>;

  userPrefs: UserPrefs;
  _dossierViewPrefs: DossiersViewPrefs;

  @ViewChild('formContainer', { read: ViewContainerRef, static: true })
  formContainer: ViewContainerRef;
  form: formPlycaComponent;
  formConfig: FormConfig;
  resourceJson = 'documentSearchFiltersISettings.json';

  constructor(
    protected documentService: DocumentService,
    protected plycaService: PlycaDataService,
    protected uiService: UIService,
    protected userService: UserService,
    protected waitService: WaitLoadingService,
    protected helpService: HelpService,
    private headerActionsService: HeaderActionsService,
    protected configuration: Configuration,
    private elem: ElementRef,
    protected dialogService: DialogService,
    protected componentFactoryResolver: ComponentFactoryResolver,
    private datePipe: DatePipe,
    protected translate: TranslateService,
    private store: Store<AppState>,
    private userPrefsService: UserPrefsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.waitService.startWaiting();
    this.loadActions();
    this.subscribe(this.userPrefsService.getUserPrefs().pipe(take(1)),
      (prefs: UserPrefs) => {
        this.userPrefs = prefs;

        let _autoLoad = false;
        if (this.userPrefs.searchDocumentsPrefs) {
          this.state = this.userPrefs.searchDocumentsPrefs;
        } else {
          this.state = new DocumentSearchStateDTO();
        }

        this.filters = lodash.cloneDeep(this.state._filters);
        this.updateActiveFilters();
        if (this.activeFilters.length > 0) {
          _autoLoad = true;
        }

        this.subscribe(this.createFiltersForm(), _loaded => {
          if (_loaded) {
            if (_autoLoad) {
              this.loadDocuments();
            } else {
              this.loaded = true;
              this.waitService.cancelWaiting();
            }
          }
        });
      });
  }

  ngOnDestroy(): void {
    try {
      this.helpService.clearContextHelp();
      this.headerActionsService.clearActions();
      this.userPrefs.searchDocumentsPrefs = this.state;
      let thisUserPrefs = this.userPrefs;
      this.store.dispatch(SET_USERPREFS({ userPrefs: thisUserPrefs }));
      super.ngOnDestroy();
    } catch { }
  }

  actionsVisibles() {
    return !this.error && this.searchPerformed;
  }

  switchFilters() {
    let newval = !this.displayFilters;
    if (newval) {
      Object.assign(this.filters, this.state._filters);
    }
    this.displayFilters = newval;
  }

  loadActions() {
    this.subscribe(this.helpService.showContextHelpObservable(HelpType.SEARCH_DOCUMENTS));
    this.headerActionsService.setTaskDescription(
      this.elem,
      'DocumentsSearch.title',
      'fas fa-search',
      true
    );

    this.subscribe(this.headerActionsService
      .addAction('General.export.csv', 'fas fa-download', 'btn-primary', this.actionsVisibles()),
      () => {
        // this.exportCSV();
      });
    this.subscribe(this.headerActionsService
      .addAction(
        'General.reload',
        'fas fa-sync-alt',
        'btn-primary',
        this.actionsVisibles()
      ),
      () => {
        this.loadDocuments();
      });
    this.subscribe(this.headerActionsService
      .addAction('General.clean', 'fas fa-undo', 'btn-secondary', this.actionsVisibles()),
      () => {
        this.clearFilters();
      });
    this.subscribe(this.headerActionsService
      .addAction('General.filters', 'fas fa-search', 'btn-secondary', true),
      () => {
        this.switchFilters();
      });
    this.headerActionsService.addDivider();
    this.subscribe(this.headerActionsService
      .addAction(
        'General.back',
        'fas fa-chevron-left',
        'btn-outline-secondary',
        true
      ),
      () => {
        this.uiService.routeBack();
      });
  }

  createFiltersForm() {
    this.formConfig = new FormConfig(undefined,
      this.resourceJson,
      this.filters
    );
    this.formConfig.functions.push({
      identifier: 'loadDocumentClassStates',
      fn: this.loadDocumentClassStates
    });
    // this.formConfig.showValidations = true;
    this.formContainer.clear();

    const childComponent = this.componentFactoryResolver.resolveComponentFactory(
      formPlycaComponent
    );
    // at this point we want the "child" component to be rendered into the app.component:
    this.form = this.formContainer.createComponent(childComponent).instance;
    this.form['formConfig'] = this.formConfig;
    this.form['manageWaitService'] = false;
    return this.form.getCompleted();
  }

  loadPaginatedDocuments(event: any) {
    let page = Math.ceil(event.first / event.rows);
    this.state.pageInfo.number = page;
    this.state.pageInfo.size = event.rows;
    this.loadDocuments();
  }

  updateFiltersAndLoadDocuments() {
    // TODO: Revisar Si el foco esta en un input y la busqueda se hace con el enter
    // hay que pinchar dos veces en un filtro para eliminarlo (la primera vez)
    let element: HTMLElement = document.getElementById('_searchButton');
    if (element != null) {
      element.focus();
    }
    // Reseteamos el puntero de las paginas
    this.state.pageInfo.number = 0;


    // ACTUALIZAR FILTROS EN PREFERENCIAS DE USUARIO
    try {
      Object.assign(this.state._filters, this.filters);
      this.userPrefs.searchDocumentsPrefs = this.state;
      let thisUserPrefs = this.userPrefs;
      this.store.dispatch(SET_USERPREFS({ userPrefs: thisUserPrefs }));
      this.updateActiveFilters();
    } catch { }
    this.loadDocuments();
  }

  loadDocuments() {
    this.waitService.startWaiting();
    this.error = false;
    this.displayFilters = false;
    this.subscribe(this.plycaService
      .query<DocumentDTO>(this.configuration.DOCUMENTS_SERVICE_URL, this.filters2String())
      .pipe(
        catchError((error: any) => {
          console.error(error);
          this.error = true;
          this.documents = [];
          return throwError(error);
        }),
        finalize(() => {
          this.loaded = true;
          this.waitService.cancelWaiting();
        })
      ),
      (param: PaginationDTO<any>) => {
        this.searchPerformed = true;
        if (param && param.content) {
          this.state.pageInfo = param.page;
          this.documents = param.content;
        } else {
          this.documents = [];
        }
      });
  }

  customSort(event: any) {
    console.log('Ordenando documentos: ', event);
    // if (event.sortField
    //   && event.sortOrder && event.sortOrder !== '1' && event.sortOrder !== '-1'
    //   && (event.sortOrder !== this.mySortOrder
    //     || event.sortField !== this.mySortField)

    // ) {
    //   // if (event.sortField && event.sortOrder) {

    //   this.orderColumns = [];

    //   this.mySortField = event.sortField;
    //   this.mySortOrder = event.sortOrder;
    //   //let campo = event.sortField;
    //   if (this.tipoExpediente === 'CONTRACTS') {
    //     if (this.mySortField === 'contractFolderId') { this.mySortField = 'division.project.contractFolderId' };

    //   }
    //   let urlParam = this.mySortField;
    //   if (event.sortOrder === -1) {
    //     urlParam = this.mySortField + ',desc';
    //   }
    //   this.orderColumns.push({
    //     code: urlParam,
    //     name: 'Columna seleccionada'
    //   }
    //   );

    //   this.loadDossiers();
    //   this.pageInfo.number = 0;
    // }
  }

  filters2String(): any { // { [param: string]: string | string[]; } {
    let _numFilters = 0;
    let params = Object.getOwnPropertyNames(this.state._filters)
      .reduce((p, key) => {
        let value = this.state._filters[key];
        if (value != undefined && value !== '') {
          _numFilters++;
          if (Array.isArray(value)) {
            return p.set(key, value.reduce(function (a, b) { return (a.identifier + ',' + b.identifier) }));
            // } else if (value instanceof MasterDataDTO) {
            //   return p.set(key, getSimpleIdentifierFromString(value.identifier));
            // } else if (value instanceof OrganizationalUnitDTO || value instanceof UserDTO ||
            //   value instanceof DivisionDTO || value instanceof DocumentClassDTO) {
          } else if (value.identifier) {
            // si no es master data, la llamada a getSimpleIdentifierFromString no tiene efecto
            return p.set(key, getSimpleIdentifierFromString(value.identifier));
          } else if (value instanceof Date) {
            // return p.set(key, '' + value.getTime());
            // return p.set(key, value.toISOString());
            return p.set(key, '' + this.datePipe.transform(value, 'dd/MM/yyyy HH:mm:ss'));
          } else {
            return p.set(key, this.state._filters[key]);
          }
        } else {
          return p;
        }
      }, new HttpParams()
      );
    // this.numFilters = _numFilters;
    // PARAMETROS DE PAGINACION
    params = params.set('size', '' + this.state.pageInfo.size).set('page', '' + this.state.pageInfo.number).set('advancedQuery', 'true');
    return params;
  }

  updateActiveFilters() {
    let params = [];
    Object.getOwnPropertyNames(this.state._filters).forEach(key => {
      let value = this.state._filters[key];
      if (value != undefined && value != '') {
        let label = this.translate.instant('DocumentsSearch.' + key);
        if (Array.isArray(value)) {
          params.push({ 'label': label, 'key': key, 'value': value });
          // } else if (value instanceof MasterDataDTO) {
          //   params.push({ 'label': label, 'key': key, 'value': value.description });
        } else if (value.identifier) {
          let desc = value.description;
          if (!desc && value.person) {
            desc = value.person.fullName;
          }
          if (!desc) {
            desc = value.name;
          }
          params.push({ 'label': label, 'key': key, 'value': desc });
        } else if (value instanceof Date) {
          // return p.set(key, '' + value.getTime());
          params.push({ 'label': label, 'key': key, 'value': this.datePipe.transform(value, 'dd-MM-yyyy') });
        } else {
          params.push({ 'label': label, 'key': key, 'value': value });
        }
      }
    });
    // this.numFilters = params.length;
    this.activeFilters = params;
  }

  removeFilter(filter) {
    console.log('REMOVING FILTER', filter);
    this.filters[filter.key] = undefined;
    this.updateFiltersAndLoadDocuments();
  }

  clearFilters() {
    this.filters = new DocumentSearchFiltersDTO();
    this.updateFiltersAndLoadDocuments();
  }

  openDocument(doc) {
    let config: DynamicDialogConfig = {
      header: doc.name,
      width: '97%',
      styleClass: 'searchStyle'
    };

    config.data = {
      'id': doc.identifier

    }
    let modal = this.dialogService.open(documentViewerComponent, config);
    this.subscribe(modal.onClose);
  }

  getDocTooltip(document): string {
    let texto = document.name;
    if (document.currentState && document.currentState.name) {
      texto = texto + '\n\n-Estado:\n' + document.currentState.name;
    }
    if (document.creationDate) {
      // texto=texto+'\n\n-Fecha Estado:\n' + moment(document.creationDate).format("YYYY-MM-DD HH:mm");
    }
    if (document.user) {
      texto =
        texto +
        '\n\n-Usuario:\n' +
        this.userService.calculateUserFullName(document.user);
    }
    return texto;
  }

  loadDocumentClassStates(model) {
    console.log('LOADSTATES', model);
  }

  isValidState(doc) {
    return (doc &&
      doc.currentState &&
      doc.currentState.identifier &&
      doc.currentState.type &&
      getSimpleIdentifierFromString(doc.currentState.identifier) != 'null');
  }
}
